import { defineStore } from 'pinia';
import { requests } from '@/common/plugins';
import { useCompanyStore } from '@/common/store/company';
import { removeFromAxiosCache, removeItemInArray, updateAxiosCache } from '@/common/utils';
import { Creative, CreativeGetOptions, CreativeTemplate, TemplateCreate } from '@/views/creatives/interfaces';

export const useTemplateStore = defineStore('template', {
  state: () => ({
    companyTemplates: [] as Creative[],
    craftsmanTemplates: [] as CreativeTemplate[],
    currentTemplate: {} as CreativeTemplate,
    currentIndex: null,
    totalItems: 0,
  }),

  actions: {
    /**
     * Fetch templates from API and set to templates
     * store variable. If the param name exists, filter
     * received data by name.
     * @param args: Template options
     */
    async fetchTemplates(args: CreativeGetOptions): Promise<void> {
      const cacheRef = `fetch-templates-${args.companies}`;

      const result = await requests.get(
        `/creatives/templates?includes=product${args.name ? `&q=name:contains:${args.name}` : ''}`,
        {
          id: `${cacheRef}${args.name ? (`:name:contains:${args.name}`) : ''}`,
          params: { ...args, includes: 'creator' },
        },
      );

      this.companyTemplates = result.items;
      this.craftsmanTemplates = result.templates.map((t) => ({ ...t, isGlobal: true }));
      this.totalItems = result.total;
    },

    /**
     * Make a POST request to create a new template using
     * creative id and company id
     * @param data - TemplateCreate info
     * @returns Template id
     */
    async createTemplate(data: TemplateCreate): Promise<string> {
      const { currentCompanyId } = useCompanyStore();
      const result: CreativeTemplate = await requests.post(
        '/creatives/templates',
        { ...data },
        {
          cache: {
            update: {
              [`fetch-templates-${currentCompanyId}`]: 'delete',
            },
          },
        },
      );

      return result.id;
    },

    /**
     * Set value to currentTemplate store variable
     * @param templateId => Creative id
     */
    selectTemplate(templateId: string, isGlobal): void {
      const array = isGlobal ? 'craftsmanTemplates' : 'companyTemplates';
      this.currentIndex = this[array].findIndex((template) => template.id === templateId);
      this.currentTemplate = this[array][this.currentIndex];
    },

    /**
     * Make a PATCH request to update the current template name
     * and update the response value with current value.
     * @param id => Template id
     */
    async updateTemplateName(name: string, companyId: string) {
      const index = this.currentIndex;
      const { id, isGlobal } = this.currentTemplate;
      const list = isGlobal ? this.craftsmanTemplates : this.companyTemplates;

      const result = await requests.patch(
        `/creatives/${id}`,
        { name, isGlobal },
        {
          cache: {
            update: {
              [`fetch-templates-${companyId}`]: (cache, res) => {
                const array = isGlobal ? 'templates' : 'items';
                return updateAxiosCache(cache, res, array, id);
              },
            },
          },
        },
      );

      // Update
      list[index].name = result.name;
      list[index].updatedAt = result.updatedAt;
    },

    /**
     * Make a DELETE request to remove a template from
     * specific list
     * @param id - Template id
     * @param isGlobal - Specific list
     */
    async deleteTemplate(id: string, isGlobal: boolean): Promise<void> {
      const { currentCompanyId } = useCompanyStore();
      const cacheList = isGlobal ? 'templates' : 'items';
      const list = isGlobal ? 'craftsmanTemplates' : 'companyTemplates';
      const urlParams = `isTemplate=true${isGlobal ? '&isGlobal=true' : ''}`;

      await requests.delete(
        `/creatives/${id}?${urlParams}`,
        {
          cache: {
            update: {
              [`fetch-templates-${currentCompanyId}`]: (cache) => removeFromAxiosCache(cache, cacheList, id),
            },
          },
        },
      );
      // Remove in array
      this[list] = removeItemInArray(this[list], id) as CreativeTemplate[];
    },
  },
});

export default useTemplateStore;
